import { Skeleton, Text, Tooltip, FLagWithText } from "components/common"
import { Separator } from "components/custom"
import { t } from "i18next"
import { isValidElement } from "react"
import { joinBytItems, localisedValue } from "utils"
import theme from "utils/themeVars"

function countryById(countries, countryId) {
  if (countryId == null || !Array.isArray(countries)) return null
  return countries.find(c => c.id === countryId || String(c.id) === String(countryId)) ?? null
}

function preferredCityParts(preferredCityIds) {
  const labels = preferredCityIds?.displayLabels
  if (Array.isArray(labels) && labels.length > 0) return labels

  const raw = preferredCityIds ? localisedValue({ object: preferredCityIds, key: "displayValue" }) || "" : ""
  const trimmed = String(raw).trim()
  if (!trimmed) return []
  return trimmed
    .split(",")
    .map(part => part.trim())
    .filter(Boolean)
}

function preferredCitiesInfotext(cities) {
  if (cities.length === 0) return undefined
  if (cities.length === 1) return cities[0]

  const [, ...tail] = cities
  return (
    <Tooltip title={tail?.join(", ")}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span className="fs14 gray-900">{cities[0]}</span>
        <span className="fs14" style={{ color: theme["primary-color"], fontWeight: 600 }}>
          {`+${tail.length}`}
        </span>
      </span>
    </Tooltip>
  )
}

function summarizePreferredLocation(profile, countries) {
  const cities = preferredCityParts(profile?.preferredCityIds)
  const prefCountry = profile?.preferredCountryId
  const fallbackName =
    prefCountry != null ? (localisedValue({ object: prefCountry, key: "displayValue" }) || "").trim() : ""

  const match = countryById(countries, prefCountry?.value)
  const countryName =
    (match != null ? (localisedValue({ object: match, key: "name" }) || "").trim() : "") || fallbackName
  const locCode = match?.locCode || match?.loc_code

  if (!countryName && cities.length === 0) {
    return { first: "", restCount: 0, tooltipTitle: "", isEmpty: true }
  }

  return {
    first: (
      <FLagWithText
        title={countryName || undefined}
        countryCode={locCode}
        text={countryName || undefined}
        infoText={preferredCitiesInfotext(cities)}
      />
    ),
    restCount: 0,
    tooltipTitle: "",
    isEmpty: false,
  }
}

export function buildInvestmentProfilingRows(profiling, countries = []) {
  const emptySummary = () => ({
    first: "",
    restCount: 0,
    tooltipTitle: "",
    isEmpty: true,
  })

  const displayValue = entity => (entity ? localisedValue({ object: entity, key: "displayValue" }) || "" : "")

  const capitalStructureSummary = (capitalStructureEntity, targetPercentEntity) => {
    const base = displayValue(capitalStructureEntity)
    const percentage =
      displayValue(targetPercentEntity) ||
      (targetPercentEntity != null && typeof targetPercentEntity !== "object" ? String(targetPercentEntity) : "")
    const items = [base, percentage + "%"].filter(Boolean)
    if (items.length === 0) return emptySummary()
    return {
      first: joinBytItems(items, <Separator />),
      restCount: 0,
      tooltipTitle: items.join(", "),
      isEmpty: false,
    }
  }

  const summaryCommaSeparatedValue = text => {
    if (text == null || String(text).trim() === "") return emptySummary()
    const parts = String(text)
      .split(",")
      .map(s => s.trim())
      .filter(Boolean)
    if (parts.length === 0) return emptySummary()
    return {
      first: parts[0],
      restCount: parts.length - 1,
      tooltipTitle: parts.slice(1).join(", "),
      isEmpty: false,
    }
  }

  const labelFromEntity = (entity, fallbackLabel) => localisedValue({ object: entity, key: "label" }) || fallbackLabel

  const profile = profiling
  const preferredLocation = summarizePreferredLocation(profile, countries)

  const freeServicesKeys = [
    "residencyRequirement",
    "propertyManagement",
    "shortTermLease",
    "longTermLease",
    "resaleFlipping",
  ]
  const freeServices = freeServicesKeys
    .filter(key => profile?.[key]?.displayValue === "Yes")
    .map(key => localisedValue({ object: profile?.[key], key: "label" }))
    ?.join(",")

  return [
    {
      key: "preferredLocation",
      label: t("Preferred Location"),
      ...preferredLocation,
    },
    {
      key: "investmentStrategy",
      label: labelFromEntity(profile?.investmentStrategy, t("Investment Strategy")),
      ...summaryCommaSeparatedValue(displayValue(profile?.investmentStrategy)),
    },
    {
      key: "assetClass",
      label: labelFromEntity(profile?.assetClass, t("Asset Class")),
      ...summaryCommaSeparatedValue(displayValue(profile?.assetClass)),
    },
    {
      key: "capitalStructure",
      label: labelFromEntity(profile?.capitalStructure, t("Capital Structure")),
      ...capitalStructureSummary(profile?.capitalStructure, profile?.targetCapitalReturnPercent),
    },
    {
      key: "freeServices",
      label: t("Free Services"),
      ...summaryCommaSeparatedValue(freeServices),
    },
  ]
}

export function GetProfilineValue({ row, loading }) {
  if (loading) {
    return <Skeleton active paragraph={{ rows: 1 }} />
  }
  if (row.isEmpty) {
    return <Text className="fs14 gray-900">{""}</Text>
  }

  const primary = isValidElement(row.first) ? row.first : <Text className="fs14 gray-900">{row.first || ""}</Text>

  return (
    <Tooltip title={row.restCount > 0 ? row.tooltipTitle : undefined}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {primary}
        {row.restCount > 0 ? (
          <Text className="fs14" style={{ color: theme["primary-color"], fontWeight: 600 }}>
            {`+${row.restCount}`}
          </Text>
        ) : null}
      </span>
    </Tooltip>
  )
}

export const interestOptions = () => [
  { label: t("Project"), key: "project", value: true },
  { label: t("Other"), key: "other", value: false },
]
